<template>
    <hts-drawer
        v-model="visible"
        :submit="handleSubmitAddShipmentSalary"
        :title="$t('labels.addDriverCostingAssumption')"
        :rules="rulesCostingInformation"
        size="large"
        @close="close">
        <div v-if="visible">
            <hts-select-category-vehicle
                v-model="categoryVehicle"
                :label="$t('labels.vehicleType')"
                required
                multiple
                :assumption-driver="true"
                prop="vehicleType" />

            <div
                v-if="items.length"
                class="overflow-x-auto">
                <div :style="{ minWidth: minWidthScroll }">
                    <div class="w-full grid-cols-9 grid gap-5 my-3">
                        <div class="col-span-2 text-left">
                            <p>
                                {{ $t('labels.fromKm') }} <span class="text-error-500">
                                    *
                                </span>
                            </p>
                        </div>
                        <div class="col-span-2 text-left">
                            <p>
                                {{ $t('labels.toKm') }} <span class="text-error-500">
                                    *
                                </span>
                            </p>
                        </div>
                        <div class="col-span-2 text-left">
                            <p>
                                {{ $t('labels.shipmentSalary') }} <span class="text-error-500">
                                    *
                                </span>
                            </p>
                        </div>
                        <div class="col-span-2 text-left">
                            <p>
                                {{ $t('labels.incurredSalaryKm') }} <span class="text-error-500">
                                    *
                                </span>
                            </p>
                        </div>
                        <div />
                    </div>

                    <div
                        v-for="(item, index) in items"
                        :key="index"
                        class="w-full grid-cols-9 grid gap-5 transition duration-300 ease-in-out align-top mb-2">
                        <div class="col-span-2">
                            <hts-form-input
                                v-model="items[index].from"
                                type="number"
                                disabled
                                name="fromKm"
                                :prop="'from' + index"
                                required />
                        </div>
                        <div class="col-span-2">
                            <hts-form-input
                                v-model="items[index].to"
                                type="number"
                                :disabled="items.length - 1 === index"
                                name="toKm"
                                :prop="'to' + index"
                                required
                                @change="editItems" />
                        </div>
                        <div class="col-span-2">
                            <hts-form-input
                                v-model="items[index].shipmentSalary"
                                type="number"
                                :disabled="index > 0"
                                name="shipmentSalary"
                                :prop="'shipmentSalary' + index"
                                required
                                @change="editItems" />
                        </div>
                        <div class="col-span-2">
                            <hts-form-input
                                v-model="items[index].incurredSalary"
                                type="number"
                                :disabled="index === 0"
                                name="incurredSalaryKm"
                                :prop="'incurredSalaryKm' + index"
                                required
                                @change="editItems" />
                        </div>
                        <div>
                            <hts-form-button
                                v-if="index !== 0"
                                class="mt-3"
                                size="mini"
                                color="error"
                                icon-name="hi-minus-circle-icon"
                                @click="deleteItems(index)">
                                {{ $t('labels.delete') }}
                            </hts-form-button>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="text-center">
                <span>
                    No data
                </span>
            </div>
            <div class="my-3">
                <hts-form-button
                    icon-name="hi-plus-icon"
                    @click="addItems">
                    {{ $t('labels.addShipmentSalary') }}
                </hts-form-button>
            </div>
        </div>
    </hts-drawer>
</template>

<script lang="ts" setup>
import { IShipmentSalary } from '../imports/form'

import screen from '~/components/utils/screen'

const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const { $formatters } = useNuxtApp()
const close = () => {
  visible.value = false
}
const visible = ref(false)
const creating = ref(false)
const categoryVehicle = ref([])
const items = ref<Array<IShipmentSalary>>([
  {
    from: 0,
    to: null,
    shipmentSalary: null,
    incurredSalary: 0
  }
])

const resetFields = () => {
  categoryVehicle.value = []

  items.value = [
    {
      from: 0,
      to: null,
      shipmentSalary: null,
      incurredSalary: 0
    }
  ]
}

const open = () => {
  visible.value = true
}

defineExpose({
  open
})

const props = defineProps({})

const emits = defineEmits(['refreshTables'])

const handleSubmitAddShipmentSalary = async () => {
  const api = $url.cost.assumptionCosting.createAssumptionDriver
  const data: any = await $api(api, { body: { categoryVehicle: categoryVehicle.value, costingInformation: items.value } })
  const resData = data?.data?.value
  if (resData?.success) {
    process.client && $toast().success($t('labels.addAssumptionDriverCostingSuccess'))
    close()
    resetFields()
    emits('refreshTables')
  }
}
const addItems = (index: number) => {
  const data = {
    from: null,
    to: null,
    shipmentSalary: null,
    incurredSalary: null
  }
  items.value.splice(items.value.length, 0, data)
  editItems()
}
const editItems = () => {
  items.value.forEach((item: IShipmentSalary, index: number) => {
    if (index > 0) {
      items.value[index].from = items.value[index - 1].to || 0
      const totalKm = (items.value[index - 1].to || 0) - (items.value[index - 1].from || 0)
      if (items.value[index - 1].to) {
        items.value[index].shipmentSalary = (items.value[index - 1].shipmentSalary || 0) + totalKm * (items.value[index - 1].incurredSalary || 0)
      }
    }
  })
}
const rulesCostingInformation = computed(() => {
  const result: { [key: string]: any } = {}
  items.value.forEach((item: any, index: number) => {
    if (index < items.value.length - 1) {
      result['to' + index] = 'requiredAutoInput|requiredMinAutoInput:' + item.from
    }
    if (index > 1) {
      const incurredSalary = items.value[index - 1].incurredSalary ? Number(items.value[index - 1].incurredSalary) : null
      result['incurredSalaryKm' + index] = `minValueAuto:${incurredSalary}`
    }
    const rulePrices = 'max_value:999999999999999'
    result['to' + index] = result['to' + index] ? result['to' + index] + `|${rulePrices}` : rulePrices
    result['from' + index] = result['from' + index] ? result['from' + index] + `|${rulePrices}` : rulePrices
    result['shipmentSalary' + index] = result['shipmentSalary' + index] ? result['shipmentSalary' + index] + `|${rulePrices}` : rulePrices
    result['incurredSalaryKm' + index] = result['incurredSalaryKm' + index] ? result['incurredSalaryKm' + index] + `|${rulePrices}` : rulePrices
  })
  return result
})

const deleteItems = (index: number) => {
  if (items.value) {
    items.value?.splice(index, 1)
    if (index !== 0 && index !== items.value?.length) {
      items.value[index].from = items.value[index - 1].to
    }
    if (items.value) {
      items.value[items.value.length - 1 || 0].to = null
    }
    editItems()
  }
}
</script>
